/**
 * What this deal still needs from the borrower.
 *
 * The panel this replaces drew a donut over `Trigger` rows that nothing ever
 * created — so it reported "Required 0 · Received 0 · Pending 0", which was the
 * honest report of a checklist that did not exist.
 *
 * The list seeds itself from the standard diligence package for the asset type
 * and loan purpose, and then stays current against the documents themselves:
 * every file on the deal, from the intake package and from the vault, is
 * matched against it. So "outstanding" is answered by what actually arrived
 * rather than by someone remembering to tick a box — and every tick names the
 * file that satisfied it, because a checklist that says the appraisal is in
 * without saying which file it is makes a claim nobody can check.
 */

import { Fragment, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useDossierEdit } from './editContext'
import { Check, ChevronDown, ChevronRight, Loader2, Plus, X } from 'lucide-react'

import {
  documentRequirements,
  type DocumentChecklist,
  type DocumentRequirement,
} from '@/services/documentRequirements'

function StatusDot({ item }: { item: DocumentRequirement }) {
  if (item.status === 'received') {
    return <Check size={11} className="shrink-0 text-emerald-400" aria-label="Received" />
  }
  if (item.status === 'waived') {
    return <span aria-label="Waived" className="shrink-0 text-[9px] text-stone-600">—</span>
  }
  return (
    <span
      aria-label={item.required ? 'Outstanding' : 'Not required'}
      className={`h-1.5 w-1.5 shrink-0 rounded-full ${item.required ? 'bg-amber-400' : 'bg-stone-700'}`}
    />
  )
}

function Row({
  item,
  busy,
  editable,
  onWaive,
  onRestore,
  onToggleRequired,
  onRemove,
}: {
  item: DocumentRequirement
  busy: boolean
  /** The checklist changes only while the deal is unlocked, like every other
   *  edit on the dossier: Waive and Add were live on a locked deal (DJA-09). */
  editable: boolean
  onWaive: () => void
  onRestore: () => void
  onToggleRequired: () => void
  onRemove: () => void
}) {
  const [open, setOpen] = useState(false)
  const evidence = item.satisfiedBy[0]
  return (
    <li className="border-b border-stone-900/70 last:border-0">
      <div className="flex items-start gap-2 py-1.5">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? `Hide detail for ${item.label}` : `Show detail for ${item.label}`}
          className="mt-0.5 shrink-0 text-stone-600 hover:text-stone-400"
        >
          {open ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
        </button>
        <StatusDot item={item} />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span
              className={`truncate text-[10px] ${
                item.status === 'received'
                  ? 'text-stone-400'
                  : item.required
                    ? 'text-stone-100'
                    : 'text-stone-500'
              }`}
            >
              {item.label}
            </span>
            {!item.required && item.status !== 'waived' ? (
              <span className="shrink-0 text-[7.5px] uppercase tracking-wide text-stone-700">
                optional
              </span>
            ) : null}
            {item.addedByHand ? (
              <span className="shrink-0 text-[7.5px] uppercase tracking-wide text-sky-500/70">
                added
              </span>
            ) : null}
          </div>
          {evidence ? (
            <div className="truncate text-[8.5px] text-emerald-500/70" title={evidence.why ?? undefined}>
              {evidence.filename}
            </div>
          ) : null}
          {item.status === 'waived' ? (
            <div className="truncate text-[8.5px] text-stone-600">
              Waived{item.waivedReason ? ` — ${item.waivedReason}` : ''}
            </div>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {busy ? <Loader2 size={10} className="animate-spin text-stone-600" /> : null}
          {!editable ? null : item.status === 'waived' ? (
            <button
              type="button"
              onClick={onRestore}
              className="text-[8px] uppercase tracking-wide text-stone-600 hover:text-stone-300"
            >
              Restore
            </button>
          ) : item.status === 'outstanding' ? (
            <button
              type="button"
              onClick={onWaive}
              title="Not needed on this deal"
              className="text-[8px] uppercase tracking-wide text-stone-600 hover:text-stone-300"
            >
              Waive
            </button>
          ) : null}
        </div>
      </div>
      {open ? (
        <div className="pb-2 pl-[38px] pr-2">
          {item.purpose ? (
            <p className="text-[8.5px] leading-relaxed text-stone-600">{item.purpose}</p>
          ) : null}
          {item.satisfiedBy.length > 0 ? (
            <ul className="mt-1 space-y-0.5">
              {item.satisfiedBy.map((e) => (
                <li key={e.filename} className="truncate text-[8px] text-stone-600">
                  <span className="text-stone-500">{e.filename}</span>
                  {e.why ? <span> — {e.why}</span> : null}
                </li>
              ))}
            </ul>
          ) : null}
          <div className={editable ? 'mt-1.5 flex items-center gap-3' : 'hidden'}>
            <button
              type="button"
              onClick={onToggleRequired}
              className="text-[8px] uppercase tracking-wide text-stone-600 hover:text-amber-300"
            >
              {item.required ? 'Make optional' : 'Make required'}
            </button>
            {item.addedByHand ? (
              <button
                type="button"
                onClick={onRemove}
                className="text-[8px] uppercase tracking-wide text-stone-600 hover:text-red-300"
              >
                Remove
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </li>
  )
}

export function RequiredDocumentsPanel({ loanId }: { loanId: string | null }) {
  const queryClient = useQueryClient()
  const [busyId, setBusyId] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [showDone, setShowDone] = useState(false)
  // A waiver says why. It used to need no reason at all, so a hole in the
  // file could never be explained later (DJA-09).
  const [waiving, setWaiving] = useState<{ id: string; reason: string } | null>(null)
  const { unlocked } = useDossierEdit()

  const checklist = useQuery({
    queryKey: ['loan', loanId, 'document-requirements'],
    queryFn: () => documentRequirements.list(loanId as string),
    enabled: Boolean(loanId),
    staleTime: 30_000,
  })

  const settle = (data: DocumentChecklist) => {
    queryClient.setQueryData(['loan', loanId, 'document-requirements'], data)
    setBusyId(null)
    setError(null)
  }
  const fail = (message: string) => { setError(message); setBusyId(null) }

  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof documentRequirements.update>[2] }) =>
      documentRequirements.update(loanId as string, id, body),
    onSuccess: settle,
    onError: () => fail('That change could not be saved.'),
  })
  const remove = useMutation({
    mutationFn: (id: string) => documentRequirements.remove(loanId as string, id),
    onSuccess: settle,
    onError: () => fail('That item could not be removed.'),
  })
  const add = useMutation({
    mutationFn: (label: string) => documentRequirements.add(loanId as string, { label }),
    onSuccess: (data) => { settle(data); setDraft(''); setAdding(false) },
    onError: () => fail('That item could not be added.'),
  })

  const data = checklist.data
  const grouped = useMemo(() => {
    const items = (data?.items ?? []).filter((i) => showDone || i.status !== 'received')
    const out: { category: string; items: DocumentRequirement[] }[] = []
    for (const item of items) {
      const last = out[out.length - 1]
      if (last && last.category === item.category) last.items.push(item)
      else out.push({ category: item.category, items: [item] })
    }
    return out
  }, [data, showDone])

  if (!loanId) return null
  if (checklist.isLoading) {
    return (
      <div className="flex items-center gap-2 py-6 text-[10px] text-stone-600">
        <Loader2 size={12} className="animate-spin" /> Reading the diligence checklist…
      </div>
    )
  }

  const received = data?.receivedCount ?? 0
  const required = data?.requiredCount ?? 0
  // What the toggle reveals: every received item, optional ones included.
  // The required-only count read 17 on a button that revealed 19.
  const receivedItems = (data?.items ?? []).filter((i) => i.status === 'received').length
  const pct = data?.completionPct

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between gap-2 pb-1.5">
        <div className="flex items-baseline gap-2">
          <span className="font-mono text-lg text-stone-100">
            {received}<span className="text-stone-600">/{required}</span>
          </span>
          <span className="text-[8.5px] text-stone-600">
            {pct === null || pct === undefined
              ? 'nothing asked of this deal yet'
              : `${pct}% of what the borrower owes us`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowDone((v) => !v)}
            className="text-[8px] uppercase tracking-wide text-stone-600 hover:text-stone-300"
          >
            {showDone ? 'Hide received' : `Show received (${receivedItems})`}
          </button>
          {unlocked ? (
            <button
              type="button"
              onClick={() => setAdding((v) => !v)}
              aria-label="Add a required document"
              className="inline-flex items-center gap-1 rounded border border-amber-400/25 px-1.5 py-0.5 text-[8px] text-amber-200 hover:bg-amber-400/[0.06]"
            >
              <Plus size={8} /> Add
            </button>
          ) : (
            <span className="text-[8px] uppercase tracking-wide text-stone-700" title="Unlock the deal to change its checklist">
              Locked
            </span>
          )}
        </div>
      </div>

      <div className="h-1 w-full overflow-hidden rounded-full bg-stone-900">
        <div
          className="h-full rounded-full bg-emerald-500/70 transition-all"
          style={{ width: `${pct ?? 0}%` }}
        />
      </div>

      {adding ? (
        <form
          className="mt-2 flex items-center gap-1.5"
          onSubmit={(e) => {
            e.preventDefault()
            const label = draft.trim()
            if (!label) return
            setBusyId('new')
            add.mutate(label)
          }}
        >
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="e.g. Franchise agreement"
            aria-label="New required document"
            className="min-w-0 flex-1 rounded border border-stone-800 bg-black/40 px-2 py-1 text-[9.5px] text-stone-200 outline-none focus:border-amber-400/40"
          />
          <button type="submit" className="rounded bg-amber-400/15 px-2 py-1 text-[8px] uppercase tracking-wide text-amber-200">
            Add
          </button>
          <button type="button" onClick={() => { setAdding(false); setDraft('') }} aria-label="Cancel" className="text-stone-600 hover:text-stone-300">
            <X size={11} />
          </button>
        </form>
      ) : null}

      {error ? <p role="alert" className="mt-1.5 text-[8.5px] text-red-300">{error}</p> : null}

      <div className="mt-1.5 min-h-0 flex-1 overflow-y-auto pr-0.5">
        {grouped.length === 0 ? (
          <p className="py-6 text-center text-[9.5px] text-stone-600">
            {received > 0
              ? 'Everything asked of the borrower has arrived.'
              : 'No documents are on this deal’s list yet.'}
          </p>
        ) : (
          grouped.map((group) => (
            <div key={group.category} className="mb-1.5">
              <div className="sticky top-0 bg-[#0d0d0d] py-0.5 font-mono text-[7.5px] uppercase tracking-widest text-stone-700">
                {group.category}
              </div>
              <ul>
                {group.items.map((item) => (
                  <Fragment key={item.id}>
                    <Row
                      item={item}
                      busy={busyId === item.id}
                      editable={unlocked}
                      onWaive={() => setWaiving({ id: item.id, reason: '' })}
                      onRestore={() => { setBusyId(item.id); update.mutate({ id: item.id, body: { status: 'outstanding' } }) }}
                      onToggleRequired={() => { setBusyId(item.id); update.mutate({ id: item.id, body: { required: !item.required } }) }}
                      onRemove={() => { setBusyId(item.id); remove.mutate(item.id) }}
                    />
                    {waiving?.id === item.id ? (
                      <li className="border-b border-stone-900/70 pb-2 pl-[38px] pr-2">
                        <form
                          className="flex items-center gap-1.5"
                          onSubmit={(e) => {
                            e.preventDefault()
                            const reason = waiving.reason.trim()
                            if (!reason) return
                            setBusyId(item.id)
                            setWaiving(null)
                            update.mutate({ id: item.id, body: { status: 'waived', waivedReason: reason } })
                          }}
                        >
                          <input
                            autoFocus
                            value={waiving.reason}
                            onChange={(e) => setWaiving({ id: item.id, reason: e.target.value })}
                            placeholder="Why is this not needed?"
                            aria-label={`Reason for waiving ${item.label}`}
                            className="min-w-0 flex-1 rounded border border-stone-800 bg-black/40 px-2 py-1 text-[9.5px] text-stone-200 outline-none focus:border-amber-400/40"
                          />
                          <button
                            type="submit"
                            disabled={!waiving.reason.trim()}
                            className="rounded bg-amber-400/15 px-2 py-1 text-[8px] uppercase tracking-wide text-amber-200 disabled:opacity-40"
                          >
                            Waive
                          </button>
                          <button type="button" onClick={() => setWaiving(null)} aria-label="Cancel" className="text-stone-600 hover:text-stone-300">
                            <X size={11} />
                          </button>
                        </form>
                      </li>
                    ) : null}
                  </Fragment>
                ))}
              </ul>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
